/**
 * Workflow definition list — `GET /workflows`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference): the grid
 * owns paging, search, sorting and filtering, so the page keeps only the column plan, the row
 * actions, one `DeleteDialog`, and the single piece of refetch state (`listVersion`).
 *
 * Two capabilities live outside the grid on purpose and are unchanged by the migration:
 *
 *  - **Execute.** `ExecuteWorkflowDialog` collects the runtime `{{input}}` value. The list DTO has
 *    no `workflowJson`, so the detail is fetched first to know whether the graph references
 *    `{{input}}` at all before the dialog opens. On success the page navigates to the workflow's
 *    detail route, which is the only place a run can be watched — and it is also the page that
 *    links on into the agent composer (`ROUTES.COMPOSER_DETAIL`), so that path is preserved.
 *  - **Tenant / project scoping.** The endpoint scopes by the ambient tenant + project, so both
 *    ids are dependencies of `dataSource`: switching either rebuilds it and the grid refetches.
 *
 * ── Column plan ──────────────────────────────────────────────────────────────────────────────────
 *  field         | header  | width | cell type          | filter
 *  --------------|---------|-------|--------------------|------------------------------------------
 *  name          | Name    |  30%  | text (clickable)   | toolbar search (searchTerm)
 *  workflowType  | Type    |  14%  | text               | —
 *  patternType   | Pattern |  14%  | text (valueGetter) | —
 *  version       | Version |   8%  | text               | —
 *  isActive      | Status  |  12%  | custom → Chip      | status tabs (synthetic → isActive)
 *  updatedAt     | Updated |  22%  | date (locale)      | —
 *
 * Dropped (present on the old MUI table): `description` (was a muted second line under the name),
 * the `Features` column of `supportsCheckpointing` / `supportsStreaming` chips (graph capabilities,
 * not list-scanning facts — they are on the detail page), `createdAt`, `tenantId` / `projectId`.
 *
 * Service filters NOT offered: `workflowService.getWorkflows` accepts only `searchTerm` and
 * `isActive`, both of which are wired above. There is no type or pattern filter and no sort
 * parameter, which is why neither has a control and no column is marked `sortable`.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Trash2 } from 'lucide-react';

import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { ExecuteWorkflowDialog } from '../components/workflows/ExecuteWorkflowDialog';
import { workflowService, type WorkflowDefinitionDto } from '../services/workflowService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';
import { workflowRequiresInput } from '../utils/workflowInput';

import './WorkflowListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to `paramKey`, and it builds each tab's test id
 * as `cordio-datatable-status-tab-<value>`. GRID-13 asserts on `-all` / `-true` / `-false`, so the
 * All tab must carry the literal `'all'` — an empty value would emit
 * `cordio-datatable-status-tab-` and the checklist would select nothing.
 *
 * That is why `paramKey` is the synthetic `status` rather than `isActive`: `'all'` is a UI concept,
 * not a value the API accepts, so {@link toWorkflowQuery} maps it (and drops it for All).
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Turns the grid's query into the one `workflowService.getWorkflows` accepts: the synthetic
 * `status` tab value becomes `isActive`, and All becomes "no filter at all" rather than a third
 * state.
 */
function toWorkflowQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, ...rest } = query;

  if (status === 'true' || status === 'false') {
    return { ...rest, isActive: status === 'true' };
  }
  return rest;
}

const workflowDetailPath = (id: string) => ROUTES.WORKFLOW_DETAIL.replace(':id', id);
const workflowEditPath = (id: string) => ROUTES.WORKFLOW_EDIT.replace(':id', id);

const errorMessage = (err: unknown, fallback: string): string => {
  const response = (err as { response?: { data?: { message?: string } } })?.response;
  return response?.data?.message ?? (err instanceof Error ? err.message : fallback);
};

export const WorkflowListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const [executeTarget, setExecuteTarget] = useState<WorkflowDefinitionDto | null>(null);
  const [executeRequiresInput, setExecuteRequiresInput] = useState(false);
  const [executing, setExecuting] = useState(false);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<WorkflowDefinitionDto>();

  const tenantId = currentTenant?.id;
  const projectId = currentProject?.id;

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<WorkflowDefinitionDto>((query) =>
        workflowService.getWorkflows(toWorkflowQuery(query)),
      ),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload. `tenantId` / `projectId` are here because the
    // endpoint is scoped by the ambient context, so switching either must refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion, tenantId, projectId],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<WorkflowDefinitionDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '30%', clickable: true },
      { field: 'workflowType', header: 'Type', type: 'text', width: '14%' },
      {
        field: 'patternType',
        header: 'Pattern',
        type: 'text',
        width: '14%',
        // `'None'` is the backend's "no pattern" sentinel, not a pattern named None; returning
        // undefined lets the grid render its own em-dash placeholder for it.
        valueGetter: (row) => (row.patternType && row.patternType !== 'None' ? row.patternType : undefined),
      },
      { field: 'version', header: 'Version', type: 'text', width: '8%' },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '12%',
        // A Chip rather than the built-in boolean cell: "Active/Inactive" is the vocabulary the
        // rest of the product uses, and it matches the status tabs above the grid.
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
      { field: 'updatedAt', header: 'Updated', type: 'date', width: '22%', dateFormat: 'locale' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search workflows by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Workflow status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  /**
   * Opens the execute dialog, but only after the detail is fetched: the list DTO has no
   * `workflowJson`, so this is the only way to know whether the graph needs a runtime input.
   */
  const handleExecuteClick = useCallback(
    async (workflow: WorkflowDefinitionDto) => {
      try {
        const detail = await workflowService.getWorkflow(workflow.id);
        setExecuteTarget(workflow);
        setExecuteRequiresInput(workflowRequiresInput(detail.workflowJson));
      } catch (err: unknown) {
        addNotification(errorMessage(err, 'Failed to load workflow'), 'error');
      }
    },
    [addNotification],
  );

  const handleExecuteConfirm = useCallback(
    async (input: string) => {
      if (!executeTarget) return;
      setExecuting(true);
      try {
        const trimmed = input.trim();
        const response = await workflowService.executeWorkflow(
          executeTarget.id,
          trimmed ? { parameters: { input: trimmed } } : undefined,
        );
        addNotification(`Workflow execution started: ${response.executionId}`, 'success');

        const targetId = executeTarget.id;
        setExecuteTarget(null);
        // The detail route is where a run can be watched — and where the link on into the agent
        // composer lives.
        navigate(workflowDetailPath(targetId));
      } catch (err: unknown) {
        addNotification(errorMessage(err, 'Failed to execute workflow'), 'error');
      } finally {
        setExecuting(false);
      }
    },
    [executeTarget, addNotification, navigate],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<WorkflowDefinitionDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View workflow',
        onClick: (row) => navigate(workflowDetailPath(row.id)),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit workflow',
        onClick: (row) => navigate(workflowEditPath(row.id)),
      },
      {
        icon: 'play',
        tooltip: 'Execute workflow',
        onClick: (row) => void handleExecuteClick(row),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete workflow',
        className: 'workflow-list__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, handleExecuteClick, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await workflowService.deleteWorkflow(pendingItem.id);
    addNotification('Workflow deleted successfully', 'success');

    setListVersion((v) => v + 1);
  }, [pendingItem, addNotification]);

  return (
    <div className="workflow-list" data-testid="workflow-list-page">
      <ShellHeader
        title="Workflows"
        description="Multi-agent graphs, their execution pattern and whether they are live."
        data-testid="workflow-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-workflow-btn"
            onClick={() => navigate(ROUTES.WORKFLOW_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create Workflow
          </Button>
        }
      />

      <div data-testid="workflow-list-grid">
        <CordioAbpPagedDataTable<WorkflowDefinitionDto>
          className="workflow-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(workflowDetailPath(row.id))}
          onCellClick={({ row }) => navigate(workflowDetailPath(row.id))}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete workflow?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="The graph and its execution history are removed. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />

      <ExecuteWorkflowDialog
        open={!!executeTarget}
        workflowName={executeTarget?.name ?? ''}
        requiresInput={executeRequiresInput}
        loading={executing}
        onClose={() => setExecuteTarget(null)}
        onConfirm={(input) => void handleExecuteConfirm(input)}
      />
    </div>
  );
};
